/**
 * BasketItemHelper
 *
 * Classe statique centralisant les méthodes pures liées aux items du panier.
 * Élimine la duplication entre ticket-basket-table-view, ticket-basket-with-details
 * et ticket-details-basket.
 */
import { DatePipe } from '@angular/common';
import { BasketRowStatusEnum } from '../enums/basketRowStatusEnum';
import { OrderStatus } from '../enums/order-status.enum';
import { DateValidationHelper } from './date.helper';
import { TicketStatusEnum } from '../enums/ticketStatusEnum';
import { RequestTypeEnum } from '../enums/request-type.enum';

export class BasketItemHelper {
  // ========================================
  // QUANTITÉ
  // ========================================

  /** Nombre d'heures dans une journée de travail (1 jour = 7h). Amené à évoluer. */
  static readonly HOURS_PER_DAY = 7;

  /**
   * Nombre de jours calendaires de la période, bornes incluses.
   * Retourne null si la période est incomplète ou invalide.
   */
  static getPeriodDays(ticket: any): number | null {
    if (!ticket?.periodStart || !ticket?.periodEnd) {
      return null;
    }
    const MS_PER_DAY = 24 * 60 * 60 * 1000;
    const start = new Date(ticket.periodStart).setHours(0, 0, 0, 0);
    const end = new Date(ticket.periodEnd).setHours(0, 0, 0, 0);
    const diff = Math.round((end - start) / MS_PER_DAY);
    return diff >= 0 ? diff + 1 : null;
  }

  /**
   * Volume horaire de la période (jours × 7h).
   * Retourne null si la période est incomplète ou invalide.
   */
  static getPeriodHours(ticket: any): number | null {
    const periodDays = BasketItemHelper.getPeriodDays(ticket);
    return periodDays !== null ? periodDays * BasketItemHelper.HOURS_PER_DAY : null;
  }

  /**
   * Ticket COMPETENCE dont le total d'heures est calculé par le back (PRED-3286) :
   * la quantité n'est plus saisie (catalogue et panier en lecture seule).
   * Faux pour les tickets COMPETENCE antérieurs, qui gardent le stepper.
   */
  static isComputedCompetence(ticket: any): boolean {
    return ticket?.requestType === RequestTypeEnum.COMPETENCE && ticket?.competenceHours != null;
  }

  /**
   * Quantité maximale autorisée (en heures) pour une ligne de panier.
   * Ticket COMPETENCE calculé : `competenceHours`. Ticket COMPETENCE legacy : volume horaire de la période.
   * Sinon : pas de plafond métier.
   */
  static getMaxQuantityHours(ticket: any): number {
    const DEFAULT_MAX = 9999;
    if (ticket?.requestType !== RequestTypeEnum.COMPETENCE) {
      return DEFAULT_MAX;
    }
    if (BasketItemHelper.isComputedCompetence(ticket)) {
      return ticket.competenceHours;
    }
    return BasketItemHelper.getPeriodHours(ticket) ?? DEFAULT_MAX;
  }

  /** Convertit une quantité en heures vers un nombre de jours (fractionnaire), selon les heures/jour du ticket. */
  static hoursToDays(hours: number, hoursPerDay: number = BasketItemHelper.HOURS_PER_DAY): number {
    return (hours ?? 0) / (hoursPerDay || BasketItemHelper.HOURS_PER_DAY);
  }

  /**
   * Applique de façon centralisée un changement de quantité à un item du panier.
   *
   * Met à jour `item.quantity` avec la nouvelle valeur (déjà bornée par le
   * quantity-stepper) et synchronise `item.quantityBasketType` du même delta
   * pour les incréments/décréments (boutons). La saisie directe (`change`) ne
   * touche pas à `quantityBasketType`, conformément au comportement historique.
   */
  static applyQuantityChange(item: any, newQuantity: number, direction: 'minus' | 'plus' | 'change'): void {
    const delta = newQuantity - (item.quantity ?? 0);
    item.quantity = newQuantity;

    if (direction === 'change') return;
    if (item.quantityBasketType === null || item.quantityBasketType === undefined) return;

    const next = item.quantityBasketType + delta;
    item.quantityBasketType = next > 0 ? next : 0;
  }

  // ========================================
  // INTERVENTION / HERITAGE DATA
  // ========================================

  /**
   * Vérifier si une intervention est requise pour un élément
   */
  static isInterventionRequired(element: any): boolean {
    return element?.interventionRequired || element?.genericPrestation?.interventionRequired || false;
  }

  /**
   * Vérifier si la configuration d'intervention est autorisée selon le statut du ticket
   */
  static canConfigureIntervention(ticketStatus: string): boolean {
    return ticketStatus === TicketStatusEnum.DRAFT;
  }

  /**
   * Obtenir les classes CSS du bouton d'intervention
   * @param ticketStatus Statut du ticket
   * @param compact Si true, utilise les classes compactes (table-view/with-details), sinon les classes étendues (details-basket)
   */
  static getInterventionButtonClasses(ticketStatus: string, compact: boolean = true): string {
    const canConfigure = BasketItemHelper.canConfigureIntervention(ticketStatus);

    if (compact) {
      return canConfigure
        ? 'w-6 h-6 leading-none flex items-center justify-center bg-orange-100 text-orange-600 hover:bg-orange-200 rounded flex-shrink-0'
        : 'w-6 h-6 leading-none flex items-center justify-center bg-gray-100 text-gray-400 rounded flex-shrink-0 cursor-not-allowed';
    }

    return canConfigure
      ? 'bg-orange-100 text-orange-600 border border-orange-300 hover:bg-orange-200'
      : 'bg-gray-100 text-gray-400 border border-gray-300 cursor-not-allowed';
  }

  /**
   * Obtenir le tooltip du bouton d'intervention
   */
  static getInterventionTooltip(ticketStatus: string): string {
    return BasketItemHelper.canConfigureIntervention(ticketStatus)
      ? 'Configurer données patrimoniales'
      : "Configuration non disponible - Le ticket n'est plus en statut brouillon";
  }

  /**
   * Obtenir le libellé du patrimoine sélectionné (bâtiments ou équipements)
   * Retourne null si aucun patrimoine n'est configuré
   */
  static getHeritageLabel(element: any): string | null {
    const data = element?.interventionHeritageData;
    if (!data || data.selectedType === 'none') {
      return null;
    }

    if (data.selectedType === 'buildings' && data.buildings?.length > 0) {
      const labels = data.buildings.map((b: any) => b.label).filter(Boolean);
      return labels.length > 0 ? `Bâtiment(s) : ${labels.join(', ')}` : null;
    }

    if (data.selectedType === 'equipments' && data.equipments?.length > 0) {
      const labels = data.equipments.map((e: any) => e.displayLabel || e.label || e.reference || null).filter(Boolean);
      return labels.length > 0 ? `Équipement(s) : ${labels.join(', ')}` : null;
    }

    return null;
  }

  // ========================================
  // EXPIRATION / DÉNONCIATION CONTRAT
  // ========================================

  /**
   * Vérifier si la date de dénonciation est aujourd'hui ou déjà dépassée
   * Comportement retenu : denunciation <= today
   */
  static isDenunciationToday(denunciationDate?: Date | string): boolean {
    if (!denunciationDate) {
      return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const denunciation = new Date(denunciationDate);
    denunciation.setHours(0, 0, 0, 0);

    return denunciation <= today;
  }

  /**
   * Vérifier si les articles d'un contrat doivent être affichés en rouge
   */
  static isContractDenouncedToday(element: any): boolean {
    return BasketItemHelper.isDenunciationToday(element?.contract?.denunciationDate);
  }

  /**
   * Vérifier si une date est dans les N prochains jours (à partir d'aujourd'hui inclus)
   */
  static isDateWithinDays(date: string | Date | undefined, days: number): boolean {
    if (!date) return false;

    const target = new Date(date);
    const today = new Date();
    const limit = new Date();
    limit.setDate(today.getDate() + days);

    target.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    limit.setHours(0, 0, 0, 0);

    return target >= today && target < limit;
  }

  /**
   * Vérifier si une ligne a une date critique du contrat qui expire dans moins de 7 jours
   */
  static isExpiringSoon(element: any): boolean {
    const dateToCheck = element?.contract?.denunciationDate ?? element?.contract?.endValidityDate;
    return BasketItemHelper.isDateWithinDays(dateToCheck, 7);
  }

  /**
   * Obtenir le nombre de jours restants avant expiration
   */
  static getExpiringDaysLeft(element: any): number | null {
    const dateToCheck = element?.contract?.denunciationDate ?? element?.contract?.endValidityDate;
    if (!dateToCheck) return null;

    const target = new Date(dateToCheck);
    const today = new Date();
    target.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const diffDays = Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays < 7 ? diffDays : null;
  }

  /**
   * Obtenir le label d'expiration
   */
  static getExpiringLabel(element: any): string {
    const days = BasketItemHelper.getExpiringDaysLeft(element);
    if (days === null) return '';
    if (days === 0) return "Expire aujourd'hui";
    if (days === 1) return 'Expire demain';
    return `Expire dans ${days}j`;
  }

  /**
   * Obtenir le tooltip d'expiration
   */
  static getExpiringTooltip(element: any): string {
    const isDenunciation = !!element?.contract?.denunciationDate;
    const dateToCheck = element?.contract?.denunciationDate ?? element?.contract?.endValidityDate;
    if (!dateToCheck) return '';
    const formatted = new Date(dateToCheck).toLocaleDateString('fr-FR');
    return isDenunciation
      ? `Date de dénonciation du contrat : ${formatted}`
      : `Fin de validité du contrat : ${formatted}`;
  }

  // ========================================
  // RÉCEPTION THÉORIQUE (lignes commandées)
  // ========================================

  /**
   * Date théorique propre à la ligne : intervention si la ligne en requiert une, sinon
   * livraison. On évite ainsi d'afficher la date de la commande, commune à tout le panier.
   */
  static getLineTheoreticalDate(element: any): Date | string | undefined {
    return BasketItemHelper.isInterventionRequired(element)
      ? (element?.theoreticalInterventionDate ?? element?.theoreticalDeliveryDate)
      : element?.theoreticalDeliveryDate;
  }

  /** Date théorique de la ligne formatée dd/MM/yyyy, ou null si absente ou invalide. */
  static formatLineTheoreticalDate(element: any): string | null {
    const validDate: Date | null = DateValidationHelper.validateDate(BasketItemHelper.getLineTheoreticalDate(element));
    return validDate ? new DatePipe('fr-FR').transform(validDate, 'dd/MM/yyyy') : null;
  }

  /** Ligne commandée dont la commande est acceptée : la réception théorique fait alors statut. */
  static isOrderedWithAcceptedOrder(element: any): boolean {
    return element?.status === BasketRowStatusEnum.ORDERED && element?.orderId?.status === OrderStatus.ACCEPTED;
  }

  /**
   * Libellé de statut d'une ligne commandée (CDC v8 « RÉCEPTION THÉORIQUE LE XX/XX/XXXX »),
   * null si la ligne n'est pas dans ce cas. Partagé entre le panier ticket et le sous-panier.
   */
  static getTheoreticalReceptionLabel(element: any): string | null {
    if (!BasketItemHelper.isOrderedWithAcceptedOrder(element)) {
      return null;
    }
    const formatted: string | null = BasketItemHelper.formatLineTheoreticalDate(element);
    return formatted ? `Réception théorique le ${formatted}` : 'Réception théorique non définie';
  }

  // ========================================
  // FRAIS ANNEXES
  // ========================================

  /**
   * Vérifier si une ligne est un frais annexe
   */
  static isAdditionalFeeRow(row: any): boolean {
    return row?.isAdditionalFee === true;
  }

  /**
   * Vérifier si l'élément a des informations additionnelles valides
   */
  static hasValidAdditionalInfo(element: any): boolean {
    return (
      element.additionalInformation &&
      typeof element.additionalInformation === 'string' &&
      element.additionalInformation.trim().length > 0
    );
  }

  /**
   * Obtenir la classe CSS pour le statut du frais annexe
   */
  static getAdditionalFeeStatusClass(isEnabled: boolean): string {
    return isEnabled ? 'status-badge-green' : 'status-badge-red';
  }

  /**
   * Vérifier si un frais annexe est de type "A définir"
   */
  static isToDefinedFee(element: any): boolean {
    return BasketItemHelper.isAdditionalFeeRow(element) && element.type === 'A définir';
  }

  /**
   * Vérifier si le prix d'un frais annexe "A définir" est éditable
   */
  static canEditFeePrice(element: any, isTicketDraft: boolean): boolean {
    return BasketItemHelper.isToDefinedFee(element) && isTicketDraft && element.isEnabled;
  }

  // ========================================
  // STATUTS / AFFICHAGE
  // ========================================

  /**
   * Obtenir la classe CSS du badge de statut (version complète, 15 statuts)
   */
  static getStatusBadgeClass(status: string): string {
    const statusMap = {
      DRAFT: 'status-badge-yellow',
      WAITING: 'status-badge-yellow',
      WAITING_AUTHORIZATION: 'status-badge-yellow',
      WAITING_SUPERVISION: 'status-badge-yellow',
      WAITING_APPRO: 'status-badge-yellow',
      EXPIRED: 'status-badge-red',
      VALIDATED: 'status-badge-green',
      ORDERED: 'status-badge-green',
      BACKORDER: 'status-badge-orange',
      RECEIVED: 'status-badge-green',
      NON_COMPLIANT: 'status-badge-red',
      AVAILABLE: 'status-badge-green',
      DISABLED: 'status-badge-red',
      SUPPLIER_HANDLED: 'status-badge-yellow',
      SUPPLIER_REJECTED: 'status-badge-red',
      // Spécifiques compétence (PRED-3107) : PROPOSÉ reprend la couleur déjà utilisée pour ce
      // statut côté API (status-badge-purple, cf. getBasketLineApiStatusClass), PAS PROPOSÉ
      // reste en attente d'arbitrage déclarant au même titre que les statuts WAITING_*.
      PROPOSITION: 'status-badge-purple',
      NO_PROPOSITION: 'status-badge-yellow',
      REPLACED: 'status-badge-red',
      ACTIVE: 'status-badge-green',
      WITHDRAWN: 'status-badge-red',
    };
    return statusMap[status] || 'status-badge-yellow';
  }

  /**
   * Libellé d'une ligne au statut ORDERED : « Réception théorique le dd/MM/yyyy »
   * (PRED-3108), ou « Réception théorique non définie » sans date valide.
   */
  static getOrderedStatusLabel(element: any): string {
    const formattedDate = BasketItemHelper.formatLineTheoreticalDate(element);
    return formattedDate ? `Réception théorique le ${formattedDate}` : 'Réception théorique non définie';
  }

  /**
   * Vérifier si un item est en rejet fournisseur (couvre aussi les lignes legacy
   * CLOSED_BY_APPROVISIONER migrées vers SUPPLIER_REJECTED, PRED-3095) — le motif
   * de fermeture éventuel est affiché en tooltip.
   */
  static isSupplierRejected(element: any): boolean {
    return element?.status === BasketRowStatusEnum.SUPPLIER_REJECTED;
  }
}
